import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { layoutAPI } from 'api/LayoutAPI'
import { useNotificationPopup } from 'Components/reusable/Notification'
import _, { capitalize } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ICVData, IItemVisibility, ILayout } from 'types/layoutInterfaces'
import ControlSections from './ControlSections'
import { usePreviewData } from './PrintPreviewProvider'

/** @notExported */
interface ILayoutControlsProps {
  /** CV data */
  data: ICVData
}

type CVDataListKey = Exclude<keyof ICVData, 'about'>

const isCVDataListKey = (key: string, data: ICVData): key is CVDataListKey => key !== 'about' && key in data

/**
 * Layout controls component.
 *
 * @returns Layout controls component.
 * @notExported
 */
const LayoutControls: React.FC<ILayoutControlsProps> = ({ data }) => {
  const { visibleLayout, setVisibleLayout } = usePreviewData()
  const { t } = useTranslation()
  const { setNotificationPopup } = useNotificationPopup()
  if (!data || !visibleLayout) return <div>{t('print.noCV')}</div>

  const setErrorNotification = () => {
    setNotificationPopup({
      message: t('error.header'),
      type: 'error',
      duration: 'long',
    })
  }

  const hideFromPreview = (checked: boolean, id: number, type: string) => {
    if (visibleLayout.ItemVisibilities?.find(item => item.type === type && item.itemId === id)) {
      setVisibleLayout({
        ...visibleLayout,
        ItemVisibilities: [
          ...visibleLayout.ItemVisibilities.map((x: IItemVisibility) => {
            if (x.itemId !== id) return x
            return { ...x, hidden: checked }
          }),
        ],
      })
    } else {
      const newVisibility = { hidden: checked, itemId: id, type: type }
      setVisibleLayout({ ...visibleLayout, ItemVisibilities: [...visibleLayout.ItemVisibilities, newVisibility] })
    }
  }

  const updateLayout = (tempLayout: ILayout, type: string, item: { id: number }, checked: boolean) => {
    if (tempLayout.ItemVisibilities.find(visibility => visibility.type === type && visibility.itemId === item.id)) {
      tempLayout = {
        ...tempLayout,
        ItemVisibilities: [
          ...tempLayout.ItemVisibilities.map(visibility => {
            if (visibility.itemId !== item.id || visibility.type !== type) return visibility
            return { ...visibility, hidden: checked }
          }),
        ],
      }
    } else {
      const newVisibility = { hidden: checked, itemId: item.id, type: type }
      tempLayout = {
        ...tempLayout,
        ItemVisibilities: [...tempLayout.ItemVisibilities, newVisibility],
      }
    }

    return tempLayout
  }

  const hideSection = (checked: boolean, sectionType: string) => {
    const items: number[] = []
    if (sectionType === 'about') {
      items.push(data.about.cvId)
      hideFromPreview(checked, data.about.cvId, sectionType)
    } else if (sectionType === 'education') {
      let tempLayout = _.cloneDeep(visibleLayout)
      const degrees: number[] = []
      const courses: number[] = []
      const certificates: number[] = []
      const competences: number[] = []

      for (const item of data.degrees) {
        degrees.push(item.id)
        tempLayout = updateLayout(tempLayout, 'degrees', item, checked)
      }

      for (const item of data.courses) {
        courses.push(item.id)
        tempLayout = updateLayout(tempLayout, 'courses', item, checked)
      }

      for (const item of data.personCertificates) {
        certificates.push(item.id)
        tempLayout = updateLayout(tempLayout, 'certificates', item, checked)
      }
      for (const item of data.personCompetences) {
        competences.push(item.id)
        tempLayout = updateLayout(tempLayout, 'competences', item, checked)
      }

      setVisibleLayout(tempLayout)

      if (degrees.length > 0 && visibleLayout && visibleLayout.id > 0) {
        try {
          layoutAPI.hideSection(visibleLayout.id, degrees, 'degrees', checked)
        } catch {
          setErrorNotification()
        }
      }

      if (courses.length > 0 && visibleLayout && visibleLayout.id > 0) {
        try {
          layoutAPI.hideSection(visibleLayout.id, courses, 'courses', checked)
        } catch {
          setErrorNotification()
        }
      }

      if (certificates.length > 0 && visibleLayout && visibleLayout.id > 0) {
        try {
          layoutAPI.hideSection(visibleLayout.id, certificates, 'certificates', checked)
        } catch {
          setErrorNotification()
        }
      }

      if (competences.length > 0 && visibleLayout && visibleLayout.id > 0) {
        try {
          layoutAPI.hideSection(visibleLayout.id, competences, 'competences', checked)
        } catch {
          setErrorNotification()
        }
      }
    } else {
      let type = sectionType
      if (sectionType === 'certificates' || sectionType === 'competences') {
        type = 'person' + capitalize(type)
      }

      const section = isCVDataListKey(type, data) ? data[type] : undefined
      if (section) {
        let tempLayout = _.cloneDeep(visibleLayout)
        for (const item of section) {
          items.push(item.id)

          tempLayout = updateLayout(tempLayout, sectionType, item, checked)
        }
        setVisibleLayout(tempLayout)
      }
    }

    if (sectionType !== 'education' && items.length > 0 && visibleLayout && visibleLayout.id > 0) {
      try {
        layoutAPI.hideSection(visibleLayout.id, items, sectionType, checked)
      } catch {
        setErrorNotification()
      }
    }
  }

  const hideItem = (checked: boolean, id: number, type: string) => {
    if (visibleLayout && visibleLayout.id > 0) {
      hideFromPreview(checked, id, type)
      try {
        layoutAPI.hideItem(visibleLayout.id, id, type, checked)
      } catch {
        setErrorNotification()
      }
    }
  }

  const hideProfileImage = (status: boolean) => {
    if (visibleLayout && visibleLayout.id) {
      setVisibleLayout({ ...visibleLayout, hideProfileImage: status })
      try {
        layoutAPI.changeProfileImage(visibleLayout.id, status)
      } catch {
        setErrorNotification()
      }
    }
  }

  const hideName = async (status: boolean) => {
    if (visibleLayout && visibleLayout.id) {
      setVisibleLayout({ ...visibleLayout, hideName: status })
      try {
        await layoutAPI.changeNameHiding(visibleLayout.id, status)
      } catch {
        setErrorNotification()
      }
    }
  }

  const hideLastUsed = async (status: boolean) => {
    if (visibleLayout && visibleLayout.id) {
      setVisibleLayout({ ...visibleLayout, hideLastUsed: status })
      try {
        await layoutAPI.changeLastUsedHiding(visibleLayout.id, status)
      } catch {
        setErrorNotification()
      }
    }
  }

  const hideSkillExperience = async (status: boolean) => {
    if (visibleLayout && visibleLayout.id) {
      setVisibleLayout({ ...visibleLayout, hideSkillExperience: status })
      try {
        await layoutAPI.changeSkillExperienceHiding(visibleLayout.id, status)
      } catch {
        setErrorNotification()
      }
    }
  }

  const showContactInfo = (status: boolean) => {
    if (visibleLayout && visibleLayout.id) {
      setVisibleLayout({ ...visibleLayout, showContactInfo: status })
      try {
        layoutAPI.toggleContactInfo(visibleLayout.id, status)
      } catch {
        setErrorNotification()
      }
    }
  }

  const showCertificateExpiry = (status: boolean) => {
    if (visibleLayout && visibleLayout.id) {
      setVisibleLayout({ ...visibleLayout, showCertificateExpiry: status })
      try {
        layoutAPI.changeCertificateExpiry(visibleLayout.id, status)
      } catch {
        setErrorNotification()
      }
    }
  }

  return (
    <>
      <Box ml={1} mb={2}>
        <FormControlLabel
          control={
            <Checkbox
              data-testid="checkbox-cvprint-layout-show-contact-info"
              onChange={() => showContactInfo(!visibleLayout.showContactInfo)}
              checked={visibleLayout.showContactInfo}
            />
          }
          label={t('print.layout.showContactInfo')}
        />
        <FormControlLabel
          control={
            <Checkbox
              data-testid="checkbox-cvprint-layout-hide-profile-image"
              onChange={() => hideProfileImage(!visibleLayout.hideProfileImage)}
              checked={visibleLayout.hideProfileImage}
            />
          }
          label={t('print.hideProfileImage')}
        />
        <FormControlLabel
          control={
            <Checkbox
              data-testid="checkbox-cvprint-layout-hide-name"
              onChange={() => hideName(!visibleLayout.hideName)}
              checked={visibleLayout.hideName}
            />
          }
          label={t('print.hideName')}
        />
        <FormControlLabel
          control={
            <Checkbox
              data-testid="checkbox-cvprint-layout-hide-last-used"
              onChange={() => hideLastUsed(!visibleLayout.hideLastUsed)}
              checked={visibleLayout.hideLastUsed}
            />
          }
          label={t('print.hideLastUsed')}
        />
        <FormControlLabel
          control={
            <Checkbox
              data-testid="checkbox-cvprint-layout-hide-skill-experience"
              onChange={() => hideSkillExperience(!visibleLayout.hideSkillExperience)}
              checked={visibleLayout.hideSkillExperience}
            />
          }
          label={t('print.hideSkillExperience')}
        />
        <FormControlLabel
          control={
            <Checkbox
              data-testid="checkbox-cvprint-layout-show-certificate-expiry"
              onChange={() => showCertificateExpiry(!visibleLayout.showCertificateExpiry)}
              checked={visibleLayout.showCertificateExpiry}
            />
          }
          label={t('print.showCertificateExpiry')}
        />
      </Box>
      <ControlSections data={data} layout={visibleLayout} hideSection={hideSection} hideItem={hideItem} />
    </>
  )
}

export default LayoutControls
